import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import apiService from '../services/api';
import config from '../config/config';
import { ArrowLeft, MessageCircle, AlertTriangle } from 'lucide-react';

const AdminConversationMessages = () => {
  const { userId, otherUserId } = useParams();
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    pages: 0
  });
  const messagesEndRef = useRef(null);

  // Verificar que el usuario sea admin
  useEffect(() => {
    if (!config.isAdmin(currentUser)) {
      navigate('/');
    }
  }, [currentUser, navigate]);

  // Cargar mensajes
  useEffect(() => {
    loadMessages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, otherUserId, pagination.page]);

  // Scroll al final cuando se cargan mensajes
  useEffect(() => {
    if (messages.length > 0 && pagination.page === 1) {
      scrollToBottom();
    }
  }, [messages, pagination.page]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadMessages = async () => {
    setLoading(true);
    try {
      const response = await apiService.getAdminConversationMessages(
        userId,
        otherUserId,
        pagination.page,
        pagination.limit
      );

      setConversation(response.conversation);
      setMessages(response.messages || []);
      setPagination({
        ...pagination,
        total: response.pagination?.total || 0,
        pages: response.pagination?.pages || 0
      });
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-MX', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading && !conversation) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando mensajes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-8">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center space-x-2 text-gray-600 hover:text-primary transition-all duration-200 hover:scale-105 active:scale-95 mb-4"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Volver a conversaciones</span>
          </button>

          {conversation && (
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center space-x-4 mb-4">
                <MessageCircle className="h-8 w-8 text-primary" />
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">
                    Conversación entre usuarios
                  </h1>
                  <p className="text-sm text-gray-600">
                    Vista de administrador - Revisión de mensajes
                  </p>
                </div>
              </div>

              {/* Participantes */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Usuario 1 */}
                <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                  <img
                    src={
                      conversation.user1.profilePicture ||
                      '/images/default-avatar.svg'
                    }
                    alt={conversation.user1.username}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                  <div>
                    <p className="font-semibold text-gray-900">
                      {conversation.user1.firstName || conversation.user1.username}
                    </p>
                    <p className="text-sm text-gray-600">
                      @{conversation.user1.username}
                    </p>
                    {conversation.user1.email && (
                      <p className="text-xs text-gray-500 mt-1">
                        {conversation.user1.email}
                      </p>
                    )}
                  </div>
                </div>

                {/* Usuario 2 */}
                <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                  <img
                    src={
                      conversation.user2.profilePicture ||
                      '/images/default-avatar.svg'
                    }
                    alt={conversation.user2.username}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                  <div>
                    <p className="font-semibold text-gray-900">
                      {conversation.user2.firstName || conversation.user2.username}
                    </p>
                    <p className="text-sm text-gray-600">
                      @{conversation.user2.username}
                    </p>
                    {conversation.user2.email && (
                      <p className="text-xs text-gray-500 mt-1">
                        {conversation.user2.email}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Stats */}
              <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center space-x-2">
                <AlertTriangle className="h-4 w-4 text-blue-700" />
                <p className="text-sm text-blue-700">
                  Total de mensajes: <strong>{pagination.total}</strong>
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Mensajes */}
        <div className="bg-white rounded-lg shadow-md">
          {messages.length === 0 ? (
            <div className="p-8 text-center">
              <MessageCircle className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-600">No hay mensajes en esta conversación</p>
            </div>
          ) : (
            <>
              <div className="p-6 space-y-4 max-h-[600px] overflow-y-auto">
                {messages.map((message) => {
                  const isSentByUser1 = message.sender.id === parseInt(userId);

                  return (
                    <div
                      key={message.id}
                      className={`flex ${
                        isSentByUser1 ? 'justify-start' : 'justify-end'
                      }`}
                    >
                      <div
                        className={`max-w-[70%] ${
                          isSentByUser1
                            ? 'bg-gray-100 rounded-lg rounded-tl-none'
                            : 'bg-primary text-white rounded-lg rounded-tr-none'
                        } p-4`}
                      >
                        {/* Remitente */}
                        <div className="flex items-center space-x-2 mb-2">
                          <img
                            src={
                              message.sender.profilePicture ||
                              '/images/default-avatar.svg'
                            }
                            alt={message.sender.username}
                            className="w-6 h-6 rounded-full object-cover"
                          />
                          <p
                            className={`text-xs font-semibold ${
                              isSentByUser1 ? 'text-gray-900' : 'text-white'
                            }`}
                          >
                            {message.sender.firstName || message.sender.username}
                          </p>
                        </div>

                        {/* Mensaje */}
                        <p
                          className={`${
                            isSentByUser1 ? 'text-gray-900' : 'text-white'
                          }`}
                        >
                          {message.message}
                        </p>

                        {/* Fecha */}
                        <p
                          className={`text-xs mt-2 ${
                            isSentByUser1 ? 'text-gray-500' : 'text-red-100'
                          }`}
                        >
                          {formatDate(message.createdAt)}
                        </p>
                      </div>
                    </div>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>

              {/* Paginación */}
              {pagination.pages > 1 && (
                <div className="border-t border-gray-200 p-4 flex justify-center space-x-2">
                  <button
                    onClick={() =>
                      setPagination({ ...pagination, page: pagination.page - 1 })
                    }
                    disabled={pagination.page === 1}
                    className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    Anterior
                  </button>
                  <span className="px-4 py-2 text-gray-700">
                    Página {pagination.page} de {pagination.pages}
                  </span>
                  <button
                    onClick={() =>
                      setPagination({ ...pagination, page: pagination.page + 1 })
                    }
                    disabled={pagination.page === pagination.pages}
                    className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    Siguiente
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminConversationMessages;

